import { ATTRIBUTE_FILTER_PLATFORMS } from '../constants/filter-criteria'
import type {
  AnalyticsFilterDto,
  AnalyticsFilterDtoMetric,
  ClientFilterDto,
  PlatformFilterDto,
  PredicateDto,
  PredicateDtoOp,
  RevenueTierFilterDto,
  SegmentCriteriaDto,
  UserFilterDto,
  UtmFilterDto,
} from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import {
  type AnalyticsCriterion,
  AnalyticsMetricEnum,
  type ClientCriterion,
  type FilterCriterion,
  FilterCriterionCategoryEnum,
  FilterOperatorEnum,
  type RevenueTierCriterion,
  SocialPlatformAttributeEnum,
  type SocialPlatformCriterion,
  type SocialPlatformFilterValue,
  type UserCriterion,
  type UserFilterFieldEnum,
  type UtmCriterion,
  type UtmParameterKeyEnum,
} from '../types/filter-criteria.types'

import {
  type CriteriaConflict,
  type MergedChildren,
  mergeSamePlatformNodes,
} from './segment-criteria-merge'
import {
  type CriteriaChild,
  isAnalyticsFilter,
  isClientFilter,
  isPlatformFilter,
  isRevenueTierFilter,
  isUserFilter,
  isUtmFilter,
  type StoredCriteria,
} from './segment-criteria-nodes'

const SCHEMA = 'creator-segment-criteria.v1' as const
const ROOT_KIND = 'group.and' as const

/** The FE enum is camelCase; the API is snake_case. */
const METRIC_TO_DTO: Record<AnalyticsMetricEnum, AnalyticsFilterDtoMetric> = {
  [AnalyticsMetricEnum.Cost]: 'cost',
  [AnalyticsMetricEnum.Revenue]: 'revenue',
  [AnalyticsMetricEnum.GrossProfit]: 'gp',
  [AnalyticsMetricEnum.Conversion]: 'conversion',
  [AnalyticsMetricEnum.OrderValue]: 'order_value',
  [AnalyticsMetricEnum.OrderValuePerClick]: 'order_value_per_click',
  [AnalyticsMetricEnum.EstRevenuePerClick]: 'est_revenue_per_click',
}

const METRIC_FROM_DTO = Object.fromEntries(
  Object.entries(METRIC_TO_DTO).map(([metric, dto]) => [dto, metric as AnalyticsMetricEnum]),
) as Record<AnalyticsFilterDtoMetric, AnalyticsMetricEnum>

/**
 * `window_days` is documented as 1-365. A cleared number input leaves a non-number behind, and a
 * stored or programmatically built criterion is under no obligation to respect the field's floor —
 * either way the request is rejected, so an out-of-range window makes the row unfinished instead.
 */
export const ANALYTICS_WINDOW_MIN_DAYS = 1
export const ANALYTICS_WINDOW_MAX_DAYS = 365

const isAnalyticsWindow = (windowDays: number | null): windowDays is number =>
  typeof windowDays === 'number' &&
  Number.isFinite(windowDays) &&
  windowDays >= ANALYTICS_WINDOW_MIN_DAYS &&
  windowDays <= ANALYTICS_WINDOW_MAX_DAYS

const toPredicate = (
  operator: FilterOperatorEnum | null,
  value: number | null,
): PredicateDto | null =>
  operator && typeof value === 'number' && Number.isFinite(value) ? { op: operator, value } : null

const OPERATOR_FROM_DTO: Record<PredicateDtoOp, FilterOperatorEnum> = {
  gte: FilterOperatorEnum.GreaterThanOrEqual,
  lte: FilterOperatorEnum.LessThanOrEqual,
  between: FilterOperatorEnum.GreaterThanOrEqual,
}

const fromPredicate = (predicate?: PredicateDto) => ({
  operator: predicate ? OPERATOR_FROM_DTO[predicate.op] : null,
  value: predicate?.value ?? null,
})

const buildPlatformChild = (criterion: SocialPlatformCriterion): PlatformFilterDto | null => {
  if (!criterion.platform) {
    return null
  }

  const filter: PlatformFilterDto = {
    kind: 'filter.platform',
    id: criterion.id,
    platform: criterion.platform,
  }

  switch (criterion.attribute) {
    case SocialPlatformAttributeEnum.Connected:
      return { ...filter, states: ['connected'] }
    case SocialPlatformAttributeEnum.Followers: {
      const predicate = toPredicate(criterion.operator, criterion.value)

      return predicate ? { ...filter, predicate } : null
    }
    default:
      return filter
  }
}

/**
 * A row the author has not finished filling in cannot be sent — the API rejects a filter with no
 * subject. Each builder returns null for an incomplete row so it is simply left out.
 */
const buildChild = (criterion: FilterCriterion): CriteriaChild | null => {
  switch (criterion.category) {
    case FilterCriterionCategoryEnum.SocialPlatform:
      return buildPlatformChild(criterion)

    case FilterCriterionCategoryEnum.Client: {
      if (!criterion.client) {
        return null
      }

      return {
        kind: 'filter.client',
        id: criterion.id,
        clients: [{ id: criterion.client.id, name: criterion.client.label }],
      } satisfies ClientFilterDto
    }

    case FilterCriterionCategoryEnum.RevenueTier: {
      if (!criterion.revenueTier) {
        return null
      }

      return {
        kind: 'filter.revenue-tier',
        id: criterion.id,
        tiers: [{ id: criterion.revenueTier.id, name: criterion.revenueTier.label }],
      } satisfies RevenueTierFilterDto
    }

    case FilterCriterionCategoryEnum.Analytics: {
      const predicate = toPredicate(criterion.operator, criterion.value)
      const windowDays = criterion.windowDays

      if (!criterion.metric || !predicate || !isAnalyticsWindow(windowDays)) {
        return null
      }

      const filter: AnalyticsFilterDto = {
        kind: 'filter.analytics',
        id: criterion.id,
        metric: METRIC_TO_DTO[criterion.metric],
        window_days: windowDays,
        predicate,
      }

      return criterion.advertiser
        ? {
            ...filter,
            advertiser: { id: criterion.advertiser.id, name: criterion.advertiser.label },
          }
        : filter
    }

    case FilterCriterionCategoryEnum.Utm: {
      if (!criterion.utmKey || !criterion.value) {
        return null
      }

      return {
        kind: 'filter.utm',
        id: criterion.id,
        key: criterion.utmKey,
        value: criterion.value,
      } satisfies UtmFilterDto
    }

    case FilterCriterionCategoryEnum.User: {
      const predicate = toPredicate(criterion.operator, criterion.value)

      if (!criterion.field || !predicate) {
        return null
      }

      return {
        kind: 'filter.user',
        id: criterion.id,
        field: criterion.field,
        predicate,
      } satisfies UserFilterDto
    }
  }
}

const buildTree = (criteria: FilterCriterion[]): MergedChildren => {
  const children = criteria
    .map(buildChild)
    .filter((child): child is CriteriaChild => child !== null)

  // The form builds a single AND root, so every platform row is merged.
  return mergeSamePlatformNodes(children)
}

export const isCriterionComplete = (criterion: FilterCriterion): boolean =>
  buildChild(criterion) !== null

export const getCriteriaConflicts = (criteria: FilterCriterion[]): CriteriaConflict[] =>
  buildTree(criteria).conflicts

/** Rows that stop the segment being saved: unfinished ones, and ones that contradict another. */
export const getBlockingCriteria = (criteria: FilterCriterion[]): FilterCriterion[] => {
  const conflictingIds = getCriteriaConflicts(criteria).flatMap(({ criterionIds }) => criterionIds)

  return criteria.filter(
    (criterion) => !isCriterionComplete(criterion) || conflictingIds.includes(criterion.id),
  )
}

/**
 * Form rows → the criteria tree the API takes. Returns null when nothing is complete enough to
 * send, which is also what "no criteria" means to the preview and save endpoints — and when rows
 * contradict each other, since no creator could match them.
 */
export const adaptCriteriaToDto = (criteria: FilterCriterion[]): SegmentCriteriaDto | null => {
  const { children, conflicts } = buildTree(criteria)

  return children.length > 0 && conflicts.length === 0
    ? { schema: SCHEMA, root: { kind: ROOT_KIND, children } }
    : null
}

type SocialPlatformCondition = Pick<SocialPlatformCriterion, 'attribute' | 'operator' | 'value'>

const EXISTING_CONDITION: SocialPlatformCondition = {
  attribute: SocialPlatformAttributeEnum.Existing,
  operator: null,
  value: null,
}

const followerCondition = (
  operator: FilterOperatorEnum,
  value: number | undefined,
): SocialPlatformCondition[] =>
  typeof value === 'number'
    ? [{ attribute: SocialPlatformAttributeEnum.Followers, operator, value }]
    : []

const followerConditions = (predicate?: PredicateDto): SocialPlatformCondition[] => {
  switch (predicate?.op) {
    case 'between':
      return [
        ...followerCondition(FilterOperatorEnum.GreaterThanOrEqual, predicate.min),
        ...followerCondition(FilterOperatorEnum.LessThanOrEqual, predicate.max),
      ]
    case 'gte':
      return followerCondition(FilterOperatorEnum.GreaterThanOrEqual, predicate.value)
    case 'lte':
      return followerCondition(FilterOperatorEnum.LessThanOrEqual, predicate.value)
    default:
      return []
  }
}

/**
 * One stored node → the simplest rows that mean the same: a status row, then a row per follower
 * bound. Redundant rows the author had do not come back. The first row keeps the node id, so saving
 * again without edits merges them into the same node; the others only need to be unique in the form.
 */
const buildPlatformCriteria = (child: PlatformFilterDto): SocialPlatformCriterion[] => {
  const platform = child.platform as SocialPlatformFilterValue
  /*
   * Connection state and follower counts are only offered on some platforms, and the row hides
   * the attribute dropdown for the rest. A stored predicate or state on one of those has nowhere
   * to show, so it is dropped here instead of staying in effect invisibly and being written back
   * on the next save. States the form has no option for are dropped the same way.
   */
  const conditions = ATTRIBUTE_FILTER_PLATFORMS.includes(platform)
    ? [
        ...(child.states?.includes('connected')
          ? [{ ...EXISTING_CONDITION, attribute: SocialPlatformAttributeEnum.Connected }]
          : []),
        ...followerConditions(child.predicate),
      ]
    : []

  return (conditions.length > 0 ? conditions : [EXISTING_CONDITION]).map((condition, index) => ({
    id: index === 0 ? child.id : crypto.randomUUID(),
    category: FilterCriterionCategoryEnum.SocialPlatform,
    platform,
    ...condition,
  }))
}

const buildCriteria = (child: CriteriaChild): FilterCriterion[] => {
  if (isPlatformFilter(child)) {
    return buildPlatformCriteria(child)
  }

  const criterion = buildCriterion(child)

  return criterion ? [criterion] : []
}

const buildCriterion = (child: CriteriaChild): FilterCriterion | null => {
  if (isClientFilter(child)) {
    const [client] = child.clients

    return {
      id: child.id,
      category: FilterCriterionCategoryEnum.Client,
      client: client ? { id: client.id, label: client.name ?? '' } : null,
    } satisfies ClientCriterion
  }

  if (isRevenueTierFilter(child)) {
    const [tier] = child.tiers

    return {
      id: child.id,
      category: FilterCriterionCategoryEnum.RevenueTier,
      revenueTier: tier ? { id: tier.id, label: tier.name ?? '' } : null,
    } satisfies RevenueTierCriterion
  }

  if (isAnalyticsFilter(child)) {
    // `ReferenceDto` carries only id and name, so the advertiser's currency does not survive a
    // round trip. `AnalyticsFields` prefers the advertiser group's currency anyway.
    return {
      id: child.id,
      category: FilterCriterionCategoryEnum.Analytics,
      advertiser: child.advertiser
        ? { id: child.advertiser.id, label: child.advertiser.name ?? '', currency: '' }
        : null,
      metric: METRIC_FROM_DTO[child.metric] ?? null,
      // The camel-casing client rewrites `window_days` on the way in; accept either.
      windowDays: child.window_days ?? (child as { windowDays?: number }).windowDays ?? null,
      ...fromPredicate(child.predicate),
    } satisfies AnalyticsCriterion
  }

  if (isUtmFilter(child)) {
    return {
      id: child.id,
      category: FilterCriterionCategoryEnum.Utm,
      utmKey: child.key as UtmParameterKeyEnum,
      value: child.value,
    } satisfies UtmCriterion
  }

  if (isUserFilter(child)) {
    return {
      id: child.id,
      category: FilterCriterionCategoryEnum.User,
      field: child.field as UserFilterFieldEnum,
      ...fromPredicate(child.predicate),
    } satisfies UserCriterion
  }

  // A filter kind this build does not know about — drop it rather than render a broken row.
  return null
}

/** The stored criteria tree → form rows, for the edit view. */
export const adaptCriteriaFromDto = (criteria?: StoredCriteria | null): FilterCriterion[] =>
  ((criteria?.root.children ?? []) as CriteriaChild[]).flatMap(buildCriteria)
